import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, normalizeStyle as _normalizeStyle, withCtx as _withCtx, mergeProps as _mergeProps, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "row gap-2 align--center" }
const _hoisted_2 = { class: "mr-2" }
const _hoisted_3 = { class: "row" }
const _hoisted_4 = { class: "flex xs12" }
const _hoisted_5 = { class: "flex xs12" }
const _hoisted_6 = { class: "row" }
const _hoisted_7 = { class: "flex xs3" }
const _hoisted_8 = { class: "flex xs9" }

import {Campaign} from "@/codes/gen/types";
import {ReactiveData} from "@/codes/types/MDOTypes";
import {DEFAULT_ITEMS_PER_PAGE, ROUTE_NAMES, VUEX_ACTIONS} from "@/codes/util/constants";
import {calculatePageCount, hasWritePermission} from "@/codes/util/functions";
import {Confirm} from "@/codes/util/swalUtil";
import {onMounted, reactive, ref} from "vue";
import {useI18n} from "vue-i18n";
import {CampaignService} from '@/codes/services/CampaignService';
import {useForm} from "vee-validate";
import store from "@/store";


export default /*#__PURE__*/_defineComponent({
  setup(__props) {

const createEmptyCampaign = function (): Campaign {
  return {enable: true} as Campaign;
}

const loadingCampaigns = ref(false);
let page = 1;
const pageCount = ref(1);
const itemsPerPage = ref(DEFAULT_ITEMS_PER_PAGE);
const campaigns = reactive<ReactiveData<Campaign[]>>({data: [] as Campaign[]});
const campaign = reactive<ReactiveData<Campaign>>({data: createEmptyCampaign()});
const action = ref<"newCampaign" | "editCampaign">("newCampaign");
const showForm = ref(false);

let pageLoaded = 0;

const form = useForm();
const formMeta = reactive(form.meta);

const i18n = useI18n();

const headers = reactive({
  data: [
    {key: 'name', label: i18n.t('register.name'), width: 200},
    {key: 'description', label: i18n.t('register.description')},
    {key: 'whatsapp', label: i18n.t('register.whatsapp')}
  ]
});

const checkHasWritePermission = function () {
  if (hasWritePermission(ROUTE_NAMES.campaigns)) {
    headers.data.push({key: 'actions', label: i18n.t('form.actions'), width: 75});
  }
}

const loadCampaigns = async function () {
  campaigns.data = [];
  loadingCampaigns.value = true;
  const service = new CampaignService();
  const campaignsEdge = await service.getAll({page: page, perPage: itemsPerPage.value});
  if (campaignsEdge) {
    pageCount.value = calculatePageCount(itemsPerPage.value, campaignsEdge.pagination?.total ?? 1);
    campaigns.data = campaignsEdge.edges;
    pageLoaded = page;
  }
  loadingCampaigns.value = false;
}
const actionWhatsApp = (whats: string | undefined) => {
  if (!whats) {
    return ''
  }
  const clearedWhats = whats.trim();
  if (clearedWhats.length < 13) {
    return `55${clearedWhats}`
  }
  return clearedWhats
}

const openWhats = function (whatsapp: string, msg?: string) {
  const whatsNumber = actionWhatsApp(whatsapp);
  const text = encodeURIComponent(msg ? msg : 'Olá')
  if (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent,
      )
  ) {
    window.open(
        `https://api.whatsapp.com/send?phone=${whatsNumber}&amp;text=${text}`,
        '_blank',
    )
  } else {
    window.open(
        `https://web.whatsapp.com/send?phone=${whatsNumber}&amp;text=${text}`,
        '_blank',
    )
  }
}

const onPageChanged = function (newPage: number) {
  page = newPage;
  loadCampaigns();
}

onMounted(() => {
  loadCampaigns();

  store.subscribeAction({
    after: (action) => {
      if (action.type === VUEX_ACTIONS.setUserInfo) {
        checkHasWritePermission();
      }
    }
  });

  checkHasWritePermission();
});

const doNew = function () {
  action.value = "newCampaign";
  showForm.value = true;
}

const edit = function (toEdit: Campaign) {
  action.value = "editCampaign";
  showForm.value = true;
  campaign.data = {
    id: toEdit.id,
    name: toEdit.name,
    description: toEdit.description,
    enable: toEdit.enable,
    whatsapp: toEdit.whatsapp
  } as Campaign;
}

const validate = async function () {
  const v = await form.validate();
  if (!v.valid) {
    return false;
  }
  return true;
}

const cancel = function () {
  if (!formMeta.value.dirty) {
    Confirm.fire({
      title: i18n.t('form.confirmCancelTitle'),
      text: i18n.t('form.confirmCancelText')
    })
        .then((result) => {
          if (result.isConfirmed) {
            campaign.data = createEmptyCampaign();
            showForm.value = false;
          }
        })
  }
}

const save = async function () {
  if (await validate()) {
    const service = new CampaignService();
    if (action.value === "newCampaign") {
      const createResult = await service.create({
        name: campaign.data.name,
        description: campaign.data.description,
        enable: campaign.data.enable,
        whatsapp: campaign.data.whatsapp && campaign.data.whatsapp.length < 13 ? `+55${campaign.data.whatsapp}` : campaign.data.whatsapp
      });
      if (createResult) {
        if (campaigns.data.length < DEFAULT_ITEMS_PER_PAGE) {
          campaigns.data = [...campaigns.data, createResult.data];
        } else {
          onPageChanged(pageLoaded + 1);
        }
        campaign.data = createEmptyCampaign();
        showForm.value = false;
      }
    } else {
      const updateResult = await service.update(campaign.data.id, {
        name: campaign.data.name,
        description: campaign.data.description,
        enable: campaign.data.enable,
        whatsapp: campaign.data.whatsapp && campaign.data.whatsapp.length < 13 ? `+55${campaign.data.whatsapp}` : campaign.data.whatsapp
      });
      if (updateResult) {
        const campaignEdit = campaigns.data.filter((u) => u.id === updateResult.data.id);
        if (campaignEdit && campaignEdit.length > 0) {
          const index = campaigns.data.indexOf(campaignEdit[0]);
          const newList = [...campaigns.data];
          newList.splice(index, 1, updateResult.data);
          campaigns.data = newList;
          campaign.data = createEmptyCampaign();
          showForm.value = false;
        }
      }
    }
  }
}

const remove = function (campaign: Campaign) {
  Confirm.fire({
    title: i18n.t('form.confirmDeleteTitle'),
    text: i18n.t('form.confirmDeleteMessage', {register: i18n.t('campaign.describe', {campaign: campaign.name})})
  })
      .then(async (result) => {
        if (result.isConfirmed) {
          const service = new CampaignService();
          const result = await service.remove(campaign.id);
          if (result && result.data.id) {
            const userRemove = campaigns.data.filter((u) => u.id === result.data.id);
            if (userRemove && userRemove.length > 0) {
              const index = campaigns.data.indexOf(userRemove[0]);
              const newList = [...campaigns.data];
              newList.splice(index, 1);
              campaigns.data = newList;
              if (campaigns.data.length === 0) {
                if (page > 1) {
                  onPageChanged(1);
                } else {
                  window.location.reload();
                }
              }
            }
          }
        }
      });
}


return (_ctx: any,_cache: any) => {
  const _component_va_button = _resolveComponent("va-button")!
  const _component_va_data_table = _resolveComponent("va-data-table")!
  const _component_base_list = _resolveComponent("base-list")!
  const _component_va_navbar_item = _resolveComponent("va-navbar-item")!
  const _component_va_navbar = _resolveComponent("va-navbar")!
  const _component_va_card_title = _resolveComponent("va-card-title")!
  const _component_va_input = _resolveComponent("va-input")!
  const _component_VField = _resolveComponent("VField")!
  const _component_va_switch = _resolveComponent("va-switch")!
  const _component_va_card_content = _resolveComponent("va-card-content")!
  const _component_va_card = _resolveComponent("va-card")!
  const _component_va_modal = _resolveComponent("va-modal")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", null, [
      _createVNode(_component_base_list, {
        page: _unref(page),
        "page-count": pageCount.value,
        "current-route": _unref(ROUTE_NAMES).campaigns,
        "page-loaded": _unref(pageLoaded),
        "show-delete": false,
        onPageChanged: onPageChanged,
        onDoNew: doNew
      }, {
        default: _withCtx(() => [
          _createVNode(_component_va_data_table, {
            items: _unref(campaigns).data,
            columns: _unref(headers).data,
            "sticky-header": "",
            loading: loadingCampaigns.value,
            "no-data-html": "Sem resultados",
            style: _normalizeStyle({
          '--va-data-table-scroll-table-color': 'var(--va-divider)',
        })
          }, {
            "cell(whatsapp)": _withCtx(({ rowData }) => [
              _createElementVNode("div", _hoisted_1, [
                _createElementVNode("span", _hoisted_2, _toDisplayString(rowData.whatsapp), 1),
                (rowData.whatsapp)
                  ? (_openBlock(), _createBlock(_component_va_button, {
                      key: 0,
                      style: {"background-color":"var(--va-success)"},
                      size: "small",
                      round: "",
                      icon: "fas fa-brands fa-whatsapp",
                      onClick: ($event: any) => (openWhats(rowData.whatsapp, ''))
                    }, null, 8, ["onClick"]))
                  : _createCommentVNode("", true)
              ])
            ]),
            "cell(actions)": _withCtx(({ rowData }) => [
              _createVNode(_component_va_button, {
                icon: "fas fa-edit",
                class: "mr-1",
                color: "info",
                size: "small",
                onClick: ($event: any) => (edit(rowData))
              }, null, 8, ["onClick"]),
              _createVNode(_component_va_button, {
                icon: "fas fa-trash",
                class: "danger",
                size: "small",
                onClick: ($event: any) => (remove(rowData))
              }, null, 8, ["onClick"])
            ]),
            _: 1
          }, 8, ["items", "columns", "loading", "style"])
        ]),
        _: 1
      }, 8, ["page", "page-count", "current-route", "page-loaded"])
    ]),
    _createVNode(_component_va_modal, {
      modelValue: showForm.value,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => (showForm.value = $event)),
      blur: "",
      "no-dismiss": "",
      "no-padding": "",
      size: "large",
      "ok-text": _ctx.$t('form.save'),
      "cancel-text": _ctx.$t('form.cancel'),
      onBeforeClose: validate,
      onCancel: cancel,
      onOk: save
    }, {
      default: _withCtx(() => [
        _createVNode(_component_va_card, null, {
          default: _withCtx(() => [
            _createVNode(_component_va_card_title, { class: "flex xs12 pa-0" }, {
              default: _withCtx(() => [
                _createVNode(_component_va_navbar, {
                  color: "info",
                  shape: "",
                  class: "mb-2"
                }, {
                  left: _withCtx(() => [
                    _createVNode(_component_va_navbar_item, null, {
                      default: _withCtx(() => [
                        _createElementVNode("h3", {
                          class: "display-3",
                          style: _normalizeStyle({
                    'color': 'var(--va-white)',
                  })
                        }, _toDisplayString(_ctx.$t(`menu.${action.value}`)), 5)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_component_va_card_content, { class: "pa-2" }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_3, [
                  _createElementVNode("div", _hoisted_4, [
                    _createVNode(_component_VField, {
                      name: "name",
                      rules: "required|min:6"
                    }, {
                      default: _withCtx(({ field, errors: nameErrors }) => [
                        _createVNode(_component_va_input, _mergeProps(field, {
                          modelValue: _unref(campaign).data.name,
                          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_unref(campaign).data.name = $event)),
                          class: "mb-4",
                          label: _ctx.$t('register.name'),
                          error: nameErrors && nameErrors.length > 0,
                          "error-messages": nameErrors
                        }), null, 16, ["modelValue", "label", "error", "error-messages"])
                      ]),
                      _: 1
                    })
                  ]),
                  _createElementVNode("div", _hoisted_5, [
                    _createVNode(_component_VField, {
                      name: "description",
                      rules: "min:6"
                    }, {
                      default: _withCtx(({ field, errors: descriptionErrors }) => [
                        _createVNode(_component_va_input, _mergeProps(field, {
                          modelValue: _unref(campaign).data.description,
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_unref(campaign).data.description = $event)),
                          class: "mb-4",
                          type: "textarea",
                          label: _ctx.$t('register.description'),
                          error: descriptionErrors && descriptionErrors.length > 0,
                          "error-messages": descriptionErrors
                        }), null, 16, ["modelValue", "label", "error", "error-messages"])
                      ]),
                      _: 1
                    })
                  ])
                ]),
                _createElementVNode("div", _hoisted_6, [
                  _createElementVNode("div", _hoisted_7, [
                    _createVNode(_component_va_switch, {
                      modelValue: _unref(campaign).data.enable,
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (_unref(campaign).data.enable = $event)),
                      "true-inner-label": _ctx.$t('form.enabled'),
                      "false-inner-label": _ctx.$t('form.disabled'),
                      class: "mr-4"
                    }, null, 8, ["modelValue", "true-inner-label", "false-inner-label"])
                  ]),
                  _createElementVNode("div", _hoisted_8, [
                    _createVNode(_component_VField, {
                      name: "whatsapp",
                      rules: 'min:9'
                    }, {
                      default: _withCtx(({ field, errors: whatsappErrors }) => [
                        _createVNode(_component_va_input, _mergeProps(field, {
                          modelValue: _unref(campaign).data.whatsapp,
                          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (_unref(campaign).data.whatsapp = $event)),
                          type: "tel",
                          class: "mb-4",
                          label: _ctx.$t('register.whatsapp'),
                          error: whatsappErrors && whatsappErrors.length > 0,
                          "error-messages": whatsappErrors,
                          messages: "Ex: +5544988776655, +34999999999 ou +351987654321"
                        }), null, 16, ["modelValue", "label", "error", "error-messages"])
                      ]),
                      _: 1
                    })
                  ])
                ])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue", "ok-text", "cancel-text"])
  ], 64))
}
}

})